import { DESTINATION_HEALTH_STATUS, DESTINATION_TYPE } from '@adtrackify/at-tracking-event-types';
import { useMemo } from 'react';

import { AGGREGATED_HEALTH_STATUS } from '../types/use-integration-health-status-enums';

import { usePixelDestinations } from '@/hooks/account/use-pixel-destinations';

interface UseDestinationHealthStatusResult {
  status: AGGREGATED_HEALTH_STATUS;
  hasUnhealthyDestinations: boolean;
  hasUnknownDestinations: boolean;
  enabledCount: number;
}

export function useDestinationHealthStatus(): UseDestinationHealthStatusResult {
  const destinations = usePixelDestinations();

  return useMemo(() => {
    // Filter to only "destination" type — exclude integrations and contact acquisitions
    const destinationOnly = destinations.filter(
      d => d.destinationType === DESTINATION_TYPE.DESTINATION
    );
    const enabledDestinations = destinationOnly.filter(d => d.enabled);

    // No enabled destinations
    if (enabledDestinations.length === 0) {
      return {
        status: 'not_connected' as AGGREGATED_HEALTH_STATUS,
        hasUnhealthyDestinations: false,
        hasUnknownDestinations: false,
        enabledCount: 0,
      };
    }

    const hasUnhealthyDestinations = enabledDestinations.some(
      d => d.healthStatus?.status === DESTINATION_HEALTH_STATUS.UNHEALTHY
    );

    const hasUnknownDestinations = enabledDestinations.some(
      d => d.healthStatus?.status === DESTINATION_HEALTH_STATUS.UNKNOWN
    );

    // Priority: unhealthy > unknown > healthy
    let status: AGGREGATED_HEALTH_STATUS = AGGREGATED_HEALTH_STATUS.HEALTHY;
    if (hasUnhealthyDestinations) {
      status = AGGREGATED_HEALTH_STATUS.UNHEALTHY;
    } else if (hasUnknownDestinations) {
      status = AGGREGATED_HEALTH_STATUS.UNKNOWN;
    }

    return {
      status,
      hasUnhealthyDestinations,
      hasUnknownDestinations,
      enabledCount: enabledDestinations.length,
    };
  }, [destinations]);
}
